import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, ShieldCheck, Eye, EyeOff, Check, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';
import { BrandLogo } from '../components/BrandLogo';

const strengthFor = (pw) => {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
};

const RestablecerPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const s = strengthFor(password);
  const strengthLabel = ['', 'Muy débil', 'Débil', 'Buena', 'Fuerte'][s];
  const strengthColor = ['bg-white/10', 'bg-red-500', 'bg-orange-500', 'bg-yellow-400', 'bg-emerald-500'][s];

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast({ title: 'Enlace inválido' }); return; }
    if (password !== confirm) { toast({ title: 'Las contraseñas no coinciden' }); return; }
    if (password.length < 8) { toast({ title: 'Contraseña muy corta', description: 'Mínimo 8 caracteres' }); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/inicio-sesion'), 2500);
    } catch (err) {
      toast({ title: 'No se pudo actualizar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
        <div className="absolute -bottom-20 -right-20 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo size="md" />
        </div>
        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 backdrop-blur">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="mt-5 text-2xl md:text-3xl font-black text-white">¡Contraseña actualizada!</h1>
              <p className="mt-3 text-white/60 text-sm">Te llevamos a inicio de sesión…</p>
            </div>
          ) : !token ? (
            <div className="text-center py-6">
              <h1 className="text-2xl font-black text-white">Enlace inválido</h1>
              <p className="mt-2 text-white/60 text-sm">Solicita un nuevo enlace de recuperación.</p>
              <Link to="/recuperar-contrasena" className="mt-4 inline-block text-orange-400 font-bold hover:text-orange-300 text-sm">Volver a recuperar contraseña</Link>
            </div>
          ) : (
            <>
              <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                Nueva <span className="text-orange-400">contraseña</span>
              </h1>
              <p className="mt-2 text-white/60 text-sm">Elige una contraseña fuerte (mínimo 8 caracteres).</p>
              <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="on" data-testid="reset-form">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Contraseña</label>
                  <div className="relative mt-2">
                    <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <Input type={showPw ? 'text' : 'password'} name="new-password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" className="h-12 pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="reset-password-input" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(s / 4) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider">{strengthLabel}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Confirmar contraseña</label>
                  <div className="relative mt-2">
                    <ShieldCheck className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <Input type={showPw ? 'text' : 'password'} name="new-password" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repite la contraseña" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="reset-confirm-input" />
                    {confirm && password === confirm && <Check className="w-4 h-4 text-emerald-400 absolute right-3.5 top-1/2 -translate-y-1/2" />}
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-13 h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-base shadow-lg shadow-orange-500/25" data-testid="reset-submit-btn">
                  {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Actualizando…</> : 'Actualizar contraseña'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestablecerPage;
